import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Bell, Sun, Moon, Menu } from "lucide-react";
import Avatar from "../ui/Avatar";

export default function Topbar({
  isLg,
  mobileNavOpen,
  setMobileNavOpen,
  collapsed,
  profileData,
  pendingCount,
  darkMode,
  setDarkMode,
  title,
}) {
  const navigate = useNavigate();
  // Unread D-series notifications (models/DoctorNotification). Counted alongside pending
  // check-in requests because the bell is one affordance: a doctor should not have to learn
  // that two different kinds of "something needs you" live behind the same icon.
  const inboxUnread = useSelector((st) => st.notifications?.unreadCount || 0);
  const badgeCount = inboxUnread + (Number(pendingCount) || 0);
  const displayName = profileData?.displayName ?? "Doctor";
  const photoUrl = profileData?.photoUrl ?? null;
  const leftOffsetLg = collapsed ? "4.5rem" : "15rem"; // rem — see Layout.jsx

  return (
    <header
      className="fixed top-0 right-0 z-50 h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 flex items-center px-3 sm:px-4 gap-2 sm:gap-3 transition-[left] duration-300 ease-in-out min-w-0 left-0"
      style={{ left: isLg ? leftOffsetLg : 0 }}
    >

      {/* Mobile menu — opens drawer; desktop uses sidebar rail toggle only */}
      <button
        type="button"
        onClick={() => setMobileNavOpen(!mobileNavOpen)}
        className="lg:hidden p-2 rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors shrink-0"
        aria-expanded={mobileNavOpen}
        aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
      >
        <Menu size={20} />
      </button>

      {/* Page title */}
      <h1 className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white truncate min-w-0 hidden sm:block">
        {title}
      </h1>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Dark mode toggle */}
      <button
        onClick={() => setDarkMode(!darkMode)}
        className="p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        {darkMode ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      {/* Notifications bell */}
      <button
        onClick={() => navigate("/notifications")}
        aria-label={badgeCount > 0 ? `Notifications, ${badgeCount} unread` : "Notifications"}
        className="relative p-2 rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <Bell size={18} />
        {badgeCount > 0 && (
          // A COUNT, not a dot: "you have things waiting" is much less useful than how many,
          // and the doctor decides whether to interrupt themselves based on the number.
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-heart-500 text-[12px] font-semibold leading-none text-white">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </button>

      {/* Avatar */}
      <button
        onClick={() => navigate("/editprofile")}
        className="flex items-center gap-2 pl-1 pr-3 h-9 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      >
        <Avatar src={photoUrl} name={displayName} size="sm" />
        <span className="hidden sm:block text-sm font-medium text-slate-700 dark:text-slate-200">
          Dr. {displayName.split(" ")[0]}
        </span>
      </button>
    </header>
  );
}
